import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import { PersonOutline, FavoriteBorder, ShoppingBagOutlined, Menu as MenuIcon, Search, ShoppingBag, Person } from '@mui/icons-material';

import { useUserContext } from '../contexts/UserContext';
import { useCartContext } from '../contexts/CartContext';
import { useShopUI } from '../contexts/ShopUIContext';
import { getUserById } from '../services/UserServices';
import useGetUserRole from '../hooks/useGetUserRole';
import useCatalog from '../hooks/useCatalog';
import { ADMIN_NAV } from './admin/AdminLayout';
import AnnouncementBar from './layout/AnnouncementBar';
import Logo from './layout/Logo';
import SearchPill from './layout/SearchPill';
import MegaMenu from './layout/MegaMenu';
import MenuDrawer from './layout/MenuDrawer';
import MobileTabBar from './layout/MobileTabBar';
import AccountDrawer from './overlays/AccountDrawer';

const USER_LINKS = [
  { label: 'My details', path: '/infos', icon: Person },
  { label: 'My orders', path: '/orders', icon: ShoppingBag }
];

const CartBubble = ({ count }) => count > 0 && (
  <Box position='absolute' top='2px' right='0' minWidth='16px' height='16px' px='4px' borderRadius='full' bg='raano.black' color='raano.white' fontSize='10px' fontWeight={700} display='flex' alignItems='center' justifyContent='center' lineHeight={1} pointerEvents='none' >
    {count > 99 ? '99+' : count}
  </Box>
);

const HeaderIcon = React.forwardRef((props, ref) => (
  <IconButton ref={ref} variant='unstyled' minWidth='40px' height='40px' display='inline-flex' alignItems='center' justifyContent='center' color='raano.black' _hover={{ opacity: 0.65 }} {...props} />
));

// Site header (design-system.md §3.2–3.3): announcement bar + 80px header with mega menu on desktop;
// 60px header + category tabs + full-screen menu + bottom tab bar on phones. Not shown on admin pages.
const Navbar = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const drawer = useDisclosure();
  const [drawerShop, setDrawerShop] = useState(false);
  const [openGenre, setOpenGenre] = useState(null);
  const closeTimer = useRef();
  const { genres, categories, products } = useCatalog();
  const { currentUser, logout: endSession } = useUserContext();
  const { itemCount } = useCartContext();
  const { openCart, openSearch } = useShopUI();
  const [admin] = useGetUserRole(currentUser);
  const accountDrawer = useDisclosure();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!currentUser) { setUser(null); return; }
    getUserById(currentUser).then(r => setUser(r.user)).catch(() => setUser(null));
  }, [currentUser]);
  const accountLinks = admin ? [{ label: 'Admin panel', path: '/admin', icon: Person }, ...ADMIN_NAV.slice(1)] : [...USER_LINKS, { label: 'Wishlist', path: '/favorites', icon: Person }];
  const activeCategory = location.pathname === '/search' ? location.state?.categoryId : null;

  const go = (path, state) => {
    drawer.onClose();
    accountDrawer.onClose();
    setOpenGenre(null);
    navigate(path, state ? { state } : undefined);
  };

  const logout = () => {
    endSession();
    go('/');
  };

  // Hover intent: small delay before closing so the pointer can travel into the panel
  const openMenu = (id) => { clearTimeout(closeTimer.current); setOpenGenre(id); };
  const closeMenu = () => { closeTimer.current = setTimeout(() => setOpenGenre(null), 120); };

  const openDrawer = (shop) => { setDrawerShop(shop); drawer.onOpen(); };

  const tabActive = accountDrawer.isOpen ? 'account' : drawer.isOpen && drawerShop ? 'categories'
    : location.pathname === '/' ? 'home'
      : location.pathname === '/favorites' ? 'wishlist'
        : ['/infos', '/orders', '/login', '/register'].includes(location.pathname) ? 'account' : null;

  const navLink = (label, onEnter, onClick, active) => (
    <Text as='button' fontSize='16px' lineHeight='40px' px='14px' color={active ? 'raano.accent' : 'raano.black'} transition='color .125s' onMouseEnter={onEnter} onClick={onClick} >{label}</Text>
  );

  return (
    <>
      <Box as='header' position='sticky' top={0} zIndex={500} bg='raano.white' >
        <AnnouncementBar />

        {/* Desktop */}
        <Box display={{ base: 'none', md: 'flex' }} position='relative' alignItems='center' height='80px' px='40px' borderBottom='1px solid' borderColor='raano.border' onMouseLeave={closeMenu} >
          <Logo />
          <Box as='nav' aria-label='Main' display='flex' alignItems='center' ml='36px' >
            {genres.map(g => (
              <Box key={g._id} onMouseLeave={closeMenu} >
                {navLink(g.name, () => openMenu(g._id), () => go('/search', {}), openGenre === g._id)}
              </Box>
            ))}
            {navLink('New In', () => setOpenGenre(null), () => go('/search', {}))}
            {navLink('Shop All', () => setOpenGenre(null), () => go('/search', {}))}
          </Box>

          <Box ml='auto' display='flex' alignItems='center' gap='12px' >
            <SearchPill onOpen={openSearch} />
            <HeaderIcon aria-label={currentUser ? 'Account' : 'Log in'} icon={<PersonOutline />} onClick={accountDrawer.onOpen} />
            <HeaderIcon aria-label='Wishlist' icon={<FavoriteBorder />} onClick={() => go('/favorites')} />
            <Box position='relative' >
              <HeaderIcon aria-label={`Cart, ${itemCount} items`} icon={<ShoppingBagOutlined />} onClick={openCart} />
              <CartBubble count={itemCount} />
            </Box>
          </Box>

          {openGenre &&
            <MegaMenu
              genre={genres.find(g => g._id === openGenre)}
              categories={categories.filter(c => c.genre === openGenre)}
              products={products}
              go={go}
              onMouseEnter={() => openMenu(openGenre)}
              onMouseLeave={closeMenu}
            />}
        </Box>

        {/* Phone */}
        <Box display={{ base: 'flex', md: 'none' }} alignItems='center' height='60px' px='6px' borderBottom='1px solid' borderColor='raano.border' >
          <HeaderIcon aria-label='Open menu' icon={<MenuIcon />} onClick={() => openDrawer(false)} />
          <Logo size='26px' ml='6px' />
          <Box ml='auto' display='flex' >
            <HeaderIcon aria-label='Search' icon={<Search />} onClick={openSearch} />
            <Box position='relative' >
              <HeaderIcon aria-label={`Cart, ${itemCount} items`} icon={<ShoppingBagOutlined />} onClick={openCart} />
              <CartBubble count={itemCount} />
            </Box>
          </Box>
        </Box>
        <Box display={{ base: 'flex', md: 'none' }} height='41px' alignItems='center' gap='22px' px='12px' overflowX='auto' borderBottom='1px solid' borderColor='raano.border' sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }} >
            {[{ _id: null, name: 'Shop All' }, ...categories].map(c => (
              <Text key={c._id || 'all'} as='button' flexShrink={0} fontSize='14px' fontWeight={activeCategory === c._id || (!c._id && location.pathname === '/search' && !activeCategory) ? 700 : 400} onClick={() => go('/search', c._id ? { categoryId: c._id } : {})} >{c.name}</Text>
            ))}
          </Box>
      </Box>

      <MobileTabBar
        active={tabActive}
        onHome={() => go('/')}
        onCategories={() => openDrawer(true)}
        onWishlist={() => go('/favorites')}
        onAccount={accountDrawer.onOpen}
      />

      <AccountDrawer isOpen={accountDrawer.isOpen} onClose={accountDrawer.onClose} user={currentUser ? user : null} links={accountLinks} onLogout={logout} />

      <MenuDrawer
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
        startInShop={drawerShop}
        categories={categories}
        products={products}
        go={go}
        account={{ loggedIn: !!currentUser, links: currentUser ? accountLinks : [], logout }}
      />
    </>
  );
};

export default Navbar;
